import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useLanding } from "@/hooks/useLanding";
import {
  strikethroughPrice,
  savingsPercent,
  isAffiliate,
  deliveryLabel,
} from "@ag5r-store/shared";
import { Panel } from "@/components/ui/Panel";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { ImageCarousel } from "@/components/ui/ImageCarousel";
import { ShareProduct } from "@/components/ui/ShareProduct";
import { VariantSelector } from "@/components/ui/VariantSelector";
import { Field, Input } from "@/components/ui/Form";
import { Badge } from "@/components/ui/Badge";
import { MonoText, CopyButton } from "@/components/ui/CopyButton";
import { EmptyState, LoadingState, ErrorState } from "@/components/ui/States";
import { formatIDR, formatDuration } from "@/lib/format";
import { ApiError } from "@/lib/api";
import type { ProductListItem } from "@ag5r-store/shared";

export function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSlug, setActiveSlug] = useState<string | null>(searchParams.get("slug"));

  const search = searchParams.get("q") ?? "";
  const category = searchParams.get("category") ?? "";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listProducts({ q: search || undefined, category: category || undefined });
      setProducts(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load catalog");
    } finally {
      setLoading(false);
    }
  }, [search, category]);

  useEffect(() => {
    load();
  }, [load]);

  // Sync activeSlug dengan URL (untuk link dari landing)
  useEffect(() => {
    const slug = searchParams.get("slug");
    if (slug) setActiveSlug(slug);
  }, [searchParams]);

  const activeProduct = useMemo(
    () => products.find((p) => p.slug === activeSlug) ?? null,
    [products, activeSlug],
  );

  function openModal(slug: string) {
    setActiveSlug(slug);
    const next = new URLSearchParams(searchParams);
    next.set("slug", slug);
    setSearchParams(next, { replace: true });
  }

  function closeModal() {
    setActiveSlug(null);
    const next = new URLSearchParams(searchParams);
    next.delete("slug");
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h1 className="section-heading">CATALOG_TERMINAL</h1>
          <h2 className="mt-3 font-sans text-3xl text-ink-primary">Browse Inventory</h2>
          <MonoText size="xs" color="ink-muted" className="block mt-1">
            {products.length} item{products.length !== 1 ? "s" : ""} available
          </MonoText>
        </div>
        <div className="w-full md:w-72">
          <Input
            type="search"
            placeholder="search products..."
            value={search}
            onChange={(e) => {
              const next = new URLSearchParams(searchParams);
              if (e.target.value) next.set("q", e.target.value);
              else next.delete("q");
              setSearchParams(next, { replace: true });
            }}
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingState label="FETCHING_CATALOG" />
      ) : error ? (
        <ErrorState error={error} onRetry={load} />
      ) : products.length === 0 ? (
        <EmptyState>No products match current filters.</EmptyState>
      ) : (
        // 4 kolom di layar lebar; gap dikecilkan agar kartu tidak menyempit
        // berlebihan dan judul produk tetap muat.
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} onOpen={() => openModal(p.slug)} />
          ))}
        </div>
      )}

      {/* Action Modal */}
      {activeProduct && (
        <ProductActionModal
          product={activeProduct}
          open={!!activeSlug}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

// ============================================================
// Product Card
// ============================================================

function ProductCard({
  product,
  onOpen,
}: {
  product: ProductListItem;
  onOpen: () => void;
}) {
  const { data: landing } = useLanding();
  const affiliate = isAffiliate(product.delivery_type);
  const minPrice = product.variants.length > 0
    ? Math.min(...product.variants.map((v) => v.price))
    : 0;
  // null saat fitur mati (discount_percent = 0) atau harga tidak valid.
  //
  // Produk affiliate tidak pernah dicoret: harga yang berlaku ditentukan
  // halaman mitra, jadi mengklaim potongan atas angka yang bukan milik kita
  // adalah klaim yang tidak bisa kita pertanggungjawabkan.
  const strike = affiliate
    ? null
    : strikethroughPrice(minPrice, landing?.discount_percent ?? 0);
  // stock_available null = produk tanpa batas stok (tautan). Produk seperti itu
  // tidak punya angka untuk ditotal, jadi totalnya null, bukan 0 — 0 berarti
  // "habis" dan akan salah menandai produk yang sebenarnya selalu tersedia.
  const tracksStock = product.variants.some((v) => v.stock_available !== null);
  const totalStock = tracksStock
    ? product.variants.reduce((sum, v) => sum + (v.stock_available ?? 0), 0)
    : null;
  const hasStock = totalStock === null || totalStock > 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left group"
      disabled={!hasStock && product.delivery_type === "ACCOUNT_CREDENTIAL"}
    >
      <Panel padded={false} className="hover:border-accent h-full flex flex-col overflow-hidden">
        {/* 4/3, bukan persegi: dengan 4 kolom, gambar persegi membuat kartu
            terlalu jangkung dan harga terdorong jauh ke bawah. */}
        <div className="aspect-[4/3] bg-panel border-b border-border overflow-hidden">
          {product.images[0] ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-accent/10">
              <span className="font-mono text-xl font-bold text-accent">
                {product.product_code.slice(0, 4)}
              </span>
            </div>
          )}
        </div>
        <div className="p-3.5 flex-1 flex flex-col">
          {/* Kartu sempit: cukup satu badge status, bukan tiga chip berjejer
              yang langsung membungkus ke baris kedua. */}
          <div className="flex items-center gap-2 flex-wrap">
            {product.is_featured && (
              <span className="font-sans text-2xs text-accent">
                <i className="fa-solid fa-star mr-1" aria-hidden />Unggulan
              </span>
            )}
            {/* Tanda affiliate mendahului tanda stok: yang perlu pembeli tahu
                lebih dulu bukan ketersediaan, melainkan bahwa transaksinya
                tidak terjadi di toko ini. */}
            {affiliate ? (
              <span className="inline-flex items-center gap-1 rounded-sm bg-accent/15 px-1.5 py-0.5
                               font-sans text-2xs font-semibold text-accent">
                <i className="fa-solid fa-up-right-from-square" aria-hidden />
                Affiliate
              </span>
            ) : totalStock === null ? (
              <span className="font-sans text-2xs text-ink-muted">
                <i className="fa-solid fa-infinity mr-1" aria-hidden />Selalu tersedia
              </span>
            ) : totalStock > 0 ? (
              <span className="font-sans text-2xs text-ink-muted">
                <i className="fa-solid fa-box mr-1" aria-hidden />
                Stok {totalStock}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-sm bg-hot/15 px-1.5 py-0.5
                               font-sans text-2xs font-semibold text-hot">
                <i className="fa-solid fa-triangle-exclamation" aria-hidden />
                Stok habis
              </span>
            )}

            {/* Disembunyikan saat nol. Produk baru yang memajang "Terjual 0"
                hanya memberi tahu pembeli bahwa belum ada yang mau. */}
            {product.sold_count > 0 && (
              <span className="font-sans text-2xs text-ink-muted">
                <i className="fa-solid fa-bag-shopping mr-1" aria-hidden />
                Terjual {product.sold_count.toLocaleString("id-ID")}
              </span>
            )}
          </div>

          <h3 className="mt-1 font-sans text-sm text-ink-primary line-clamp-2
                         group-hover:text-accent transition-colors">
            {product.name}
          </h3>

          {product.description && (
            <p className="mt-1.5 font-sans text-xs text-ink-muted line-clamp-2">
              {product.description}
            </p>
          )}

          <div className="mt-auto pt-3 flex items-end justify-between gap-2">
            <div className="min-w-0">
              {affiliate && product.variants.length === 0 ? (
                // Produk affiliate boleh tidak punya varian sama sekali —
                // harganya milik mitra. "Rp 0" akan terbaca sebagai gratis.
                <p className="font-mono text-sm text-accent truncate">Cek di mitra</p>
              ) : (
                <>
                  <p className="font-sans text-2xs text-ink-dim">mulai</p>
                  {/* Harga coret hanya tampilan — yang ditagih tetap minPrice. */}
                  {strike && (
                    <p className="font-sans text-2xs text-ink-dim line-through leading-none">
                      {formatIDR(strike)}
                    </p>
                  )}
                  <p className="font-mono text-sm text-accent truncate">{formatIDR(minPrice)}</p>
                </>
              )}
            </div>
            {affiliate ? (
              <p className="font-sans text-2xs text-ink-muted shrink-0">via mitra ↗</p>
            ) : strike ? (
              <span className="shrink-0 rounded-sm bg-hot/15 px-1.5 py-0.5
                               font-sans text-2xs font-semibold text-hot">
                -{savingsPercent(minPrice, strike)}%
              </span>
            ) : (
              <p className="font-sans text-2xs text-ink-muted shrink-0">
                {product.variants.length} paket
              </p>
            )}
          </div>
        </div>
      </Panel>
    </button>
  );
}

// ============================================================
// Action Modal — variant selector + checkout
// ============================================================

function ProductActionModal({
  product,
  open,
  onClose,
}: {
  product: ProductListItem;
  open: boolean;
  onClose: () => void;
}) {
  const { addLine, toggleWishlist, isWishlisted } = useCart();
  const navigate = useNavigate();
  const [variantId, setVariantId] = useState<string | null>(
    product.variants[0]?.id ?? null,
  );
  const [added, setAdded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset selection when product changes
  useEffect(() => {
    setVariantId(product.variants[0]?.id ?? null);
    setError(null);
  }, [product.id]);

  const selectedVariant = product.variants.find((v) => v.id === variantId);

  /**
   * Produk affiliate berhenti di sini: tidak masuk keranjang, tidak dibayar,
   * tidak menerbitkan lisensi. Satu-satunya aksinya adalah membuka halaman
   * mitra yang ditentukan owner.
   */
  const affiliate = isAffiliate(product.delivery_type);
  const affiliateUrl = product.affiliate_url;

  /** stock_available null = produk tanpa stok terbatas (serial key / link). */
  const outOfStock =
    !!selectedVariant &&
    selectedVariant.stock_available !== null &&
    selectedVariant.stock_available <= 0;
  const canBuy = !!selectedVariant && !outOfStock;

  function toCartLine() {
    if (!selectedVariant) return null;
    return {
      variant_id: selectedVariant.id,
      product_slug: product.slug,
      product_name: product.name,
      variant_name: selectedVariant.name,
      unit_price: selectedVariant.price,
      delivery_type: product.delivery_type,
      image: product.images[0],
    };
  }

  function handleAddToCart() {
    const line = toCartLine();
    if (!line) return;
    addLine(line);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 2000);
  }

  function handleBuyNow() {
    const line = toCartLine();
    if (!line) return;
    addLine(line);
    onClose();
    navigate("/checkout");
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={product.name}
      subtitle={`CODE: ${product.product_code} | TYPE: ${deliveryLabel(product.delivery_type)}`}
      maxWidth="max-w-4xl"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() =>
              toggleWishlist({
                product_slug: product.slug,
                product_name: product.name,
                image: product.images[0],
              })
            }
          >
            {isWishlisted(product.slug) ? "♥ Tersimpan" : "♡ Simpan ke Wishlist"}
          </Button>
          {affiliate ? (
            // Tautan sungguhan, bukan tombol ber-onClick: pembeli berhak
            // melihat tujuannya lewat hover dan membukanya di tab baru sendiri.
            // rel sponsored+nofollow adalah penandaan yang diminta Google untuk
            // tautan affiliate.
            <a
              href={affiliateUrl ?? "#"}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              aria-disabled={!affiliateUrl}
              onClick={(e) => { if (!affiliateUrl) e.preventDefault(); }}
              className={`btn btn-primary ${affiliateUrl ? "" : "opacity-50 pointer-events-none"}`}
            >
              Lanjut ke Mitra ↗
            </a>
          ) : (
            <>
              <Button variant="secondary" onClick={handleAddToCart} disabled={!canBuy}>
                {added ? "✓ Masuk keranjang" : "+ Keranjang"}
              </Button>
              <Button variant="primary" onClick={handleBuyNow} disabled={!canBuy}>
                {outOfStock ? "Stok Habis" : "Beli Sekarang →"}
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left: Image + metadata */}
        <div>
          <ImageCarousel images={product.images} alt={product.name} />
          {product.description && (
            <div className="mt-4">
              <div className="terminal-label mb-2">DESCRIPTION</div>
              <p className="font-mono text-xs text-ink-muted leading-relaxed whitespace-pre-wrap">
                {product.description}
              </p>
            </div>
          )}
          {/* Kolom ketiga hanya muncul kalau ada yang benar-benar terjual —
              lihat alasan yang sama di kartu katalog. */}
          <div className={`mt-4 grid gap-2 mono-box p-3 ${
            product.sold_count > 0 ? "grid-cols-3" : "grid-cols-2"
          }`}>
            <div>
              <MonoText size="xs" color="ink-dim">DELIVERY</MonoText>
              <MonoText size="xs" color="terminal" className="block">
                {deliveryLabel(product.delivery_type)}
              </MonoText>
            </div>
            <div>
              <MonoText size="xs" color="ink-dim">TIERS</MonoText>
              <MonoText size="xs" color="terminal" className="block">
                {product.variants.length} available
              </MonoText>
            </div>
            {product.sold_count > 0 && (
              <div>
                <MonoText size="xs" color="ink-dim">TERJUAL</MonoText>
                <MonoText size="xs" color="terminal" className="block">
                  {product.sold_count.toLocaleString("id-ID")} unit
                </MonoText>
              </div>
            )}
          </div>

          <ShareProduct slug={product.slug} name={product.name} />
        </div>

        {/* Right: Variant selector + checkout info */}
        <div>
          {/* Produk affiliate: tidak ada tier untuk dipilih dan tidak ada total
              untuk dibayar di sini. Menampilkan "ORDER SUMMARY" akan membuat
              pembeli mengira transaksinya terjadi di toko ini. */}
          {affiliate ? (
            <div className="mono-box p-4">
              <div className="terminal-label mb-3">
                <i className="fa-solid fa-up-right-from-square mr-1.5" aria-hidden />
                PRODUK AFFILIATE
              </div>
              <p className="font-sans text-sm text-ink-muted leading-relaxed">
                Produk ini dijual oleh mitra, bukan oleh kami. Tombol
                <span className="text-ink-primary"> Lanjut ke Mitra </span>
                membuka halaman mereka di tab baru — harga akhir, pembayaran,
                dan garansi mengikuti ketentuan mitra tersebut.
              </p>
              {affiliateUrl ? (
                <MonoText size="xs" color="ink-dim" className="block mt-3 break-all">
                  {affiliateUrl}
                </MonoText>
              ) : (
                <div className="mt-3 border border-status-pending p-3">
                  <MonoText size="xs" color="status-pending">
                    Link mitra belum diisi. Hubungi admin toko.
                  </MonoText>
                </div>
              )}
              {product.variants.length > 0 && (
                <MonoText size="xs" color="ink-dim" className="block mt-3">
                  Harga di katalog ini hanya perkiraan dari mitra, bukan tagihan kami.
                </MonoText>
              )}
            </div>
          ) : (
            <>
              <div className="terminal-label mb-2">SELECT LICENSE TIER</div>
              <VariantSelector
                variants={product.variants}
                value={variantId}
                onChange={setVariantId}
              />

              {/* Order summary */}
              {selectedVariant && (
                <div className="mt-4 mono-box p-4">
                  <div className="terminal-label mb-3">ORDER SUMMARY</div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between">
                      <MonoText size="xs" color="ink-muted">item</MonoText>
                      <MonoText size="xs" color="ink-primary">{product.name}</MonoText>
                    </div>
                    <div className="flex justify-between">
                      <MonoText size="xs" color="ink-muted">variant</MonoText>
                      <MonoText size="xs" color="ink-primary">{selectedVariant.name}</MonoText>
                    </div>
                    <div className="flex justify-between">
                      <MonoText size="xs" color="ink-muted">duration</MonoText>
                      <MonoText size="xs" color="ink-primary">
                        {formatDuration(selectedVariant.duration_days)}
                      </MonoText>
                    </div>
                    <div className="border-t border-border-strong my-2" />
                    <div className="flex justify-between items-baseline">
                      <MonoText size="xs" color="ink-muted">TOTAL</MonoText>
                      <MonoText size="base" color="terminal" className="font-bold">
                        {formatIDR(selectedVariant.price)}
                      </MonoText>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-4 border border-status-failed p-3">
                  <MonoText size="xs" color="status-failed">! {error}</MonoText>
                </div>
              )}

              {outOfStock && (
                <div className="mt-4 border border-status-pending p-3">
                  <MonoText size="xs" color="status-pending">
                    Stok varian ini sedang habis. Pilih varian lain atau cek lagi nanti.
                  </MonoText>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}